import { useState, useEffect, useRef, MutableRefObject } from 'react';

/**
 * Hook personalizado para Intersection Observer
 * Para lazy loading y animaciones basadas en visibilidad
 */
export const useIntersectionObserver = (
  options: IntersectionObserverInit = {}
): [MutableRefObject<null>, boolean] => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const currentElement = elementRef.current;
    
    if (!currentElement || !('IntersectionObserver' in window)) {
      // Fallback para navegadores sin soporte
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        rootMargin: '150px 0px',
        threshold: 0.1,
        ...options
      }
    );

    observer.observe(currentElement);

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [options]);

  return [elementRef, isIntersecting];
};

/**
 * Hook para animaciones de entrada
 */
export const useAnimateOnView = (delay = 0) => {
  const [ref, isInView] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '50px 0px'
  });
  
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (isInView && !shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isInView, shouldAnimate, delay]);

  return [ref, shouldAnimate] as const;
};